import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import {
  PageHeader,
  SectionHeader,
  Kpi,
  Card,
  RangeSelect,
  IncludeTestToggle,
  TestDataBanner,
  useIncludeTest,
} from "./adminUi";
import { ArrowUpDown, Search } from "lucide-react";

const SPONSOR_TYPES = [
  ["", "All types"],
  ["church", "Church"],
  ["christian_retailer", "Christian Retailer"],
  ["bookstore", "Bookstore / Gift Store"],
  ["ministry", "Ministry"],
  ["school", "Christian School"],
  ["nonprofit", "Nonprofit"],
  ["publisher", "Publisher / Media"],
  ["conference", "Conference / Event"],
  ["corporate_partner", "Corporate Partner"],
  ["other", "Other"],
];

const COLUMNS = [
  ["sponsor_name", "Sponsor"],
  ["sponsor_type", "Type"],
  ["audience_size", "Audience"],
  ["landing_visits", "Visits"],
  ["referral_clicks", "Clicks"],
  ["signups", "Signups"],
  ["active_users", "Active"],
  ["total_reach_rate", "Reach %"],
  ["active_adoption_rate", "Adoption %"],
  ["trials", "Trials"],
  ["paid", "Paid"],
  ["gifts", "Gifts"],
  ["conversion_rate", "Conv %"],
  ["last_activity", "Last activity"],
];

export default function AdminSponsorAnalyticsPage() {
  const [range, setRange] = useState("30d");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [includeTest, setIncludeTest] = useIncludeTest();
  const [rows, setRows] = useState(null);
  const [overview, setOverview] = useState(null);
  const [sort, setSort] = useState({ key: "paid", dir: "desc" });

  useEffect(() => {
    setRows(null);
    adminApi
      .performance({ range, sponsor_type: type || undefined, include_test: includeTest })
      .then(setRows)
      .catch(() => setRows([]));
    adminApi.masterOverview({ include_test: includeTest }).then(setOverview).catch(() => {});
  }, [range, type, includeTest]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    let list = rows;
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((r) => r.sponsor_name.toLowerCase().includes(term));
    const { key, dir } = sort;
    const factor = dir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return factor * (av - bv);
      return factor * String(av).localeCompare(String(bv));
    });
    return list;
  }, [rows, q, sort]);

  const sp = overview?.sponsors || {};
  const backend = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("lg_token") : null;
  const downloadCsv = async () => {
    const url = `${backend}/api/admin/export.csv${range ? "?range=" + range : ""}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "sponsor-performance.csv";
    a.click();
  };

  return (
    <div data-testid="admin-sponsor-analytics-page" className="space-y-8">
      <PageHeader
        eyebrow="Sponsor analytics"
        title="Sponsors"
        subtitle="Reach, adoption, attribution, and revenue by sponsor. Sort or search below."
        right={<IncludeTestToggle value={includeTest} onChange={setIncludeTest} />}
      />

      <TestDataBanner show={includeTest} />

      <section>
        <SectionHeader label="Headline" />
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
          <Kpi testid="sp-total" label="Total sponsors" value={sp.total} />
          <Kpi testid="sp-active" label="Active sponsors" value={sp.active} />
          <Kpi testid="sp-attr-users" label="Attributed users" value={sp.attributed_users} />
          <Kpi testid="sp-attr-active" label="Attributed active users" value={sp.attributed_active_users} />
          <Kpi testid="sp-attr-paid" label="Attributed paid users" value={sp.attributed_paid_users} tone="accent" />
          <Kpi
            testid="sp-adopt"
            label="Avg. active adoption"
            value={sp.avg_active_adoption_rate == null ? "—" : `${sp.avg_active_adoption_rate}%`}
          />
          <Kpi testid="sp-attr-gifts" label="Sponsor-generated gifts" value={sp.attributed_gifts} />
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-end gap-3 justify-between">
          <div className="flex flex-wrap items-end gap-3">
            <RangeSelect value={range} onChange={setRange} testid="sp-range" />
            <label className="text-xs text-[#A9BFA9] inline-flex items-center gap-2">
              Type
              <select
                data-testid="sp-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="bg-[#141B18] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white"
              >
                {SPONSOR_TYPES.map(([v, l]) => (
                  <option key={v || "all"} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-2 bg-[#141B18] border border-white/10 rounded-full px-3 py-1.5">
              <Search size={12} className="text-[#8FA894]" />
              <input
                data-testid="sp-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search sponsor…"
                className="bg-transparent text-xs text-white placeholder-[#607467] outline-none w-40"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/sponsors"
              data-testid="sp-manage-link"
              className="text-xs text-[#B7D2AF] hover:text-white"
            >
              Manage sponsors →
            </Link>
            <button
              data-testid="sp-download-csv"
              onClick={downloadCsv}
              className="text-xs text-[#B7D2AF] hover:text-white underline underline-offset-2"
            >
              Download CSV
            </button>
          </div>
        </div>

        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[#8FA894] text-[10px] uppercase tracking-[0.14em]">
              <tr>
                {COLUMNS.map(([key, label]) => (
                  <th
                    key={key}
                    className="text-left px-3 py-3 whitespace-nowrap cursor-pointer select-none"
                    onClick={() =>
                      setSort((s) => ({
                        key,
                        dir: s.key === key && s.dir === "desc" ? "asc" : "desc",
                      }))
                    }
                    data-testid={`sp-th-${key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {label}
                      <ArrowUpDown size={10} className={sort.key === key ? "text-white" : "text-[#607467]"} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered === null && (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-4 py-6 text-[#8FA894]">
                    Loading…
                  </td>
                </tr>
              )}
              {filtered && filtered.length === 0 && (
                <tr>
                  <td
                    data-testid="sp-empty-row"
                    colSpan={COLUMNS.length}
                    className="px-4 py-8 text-center text-[#8FA894]"
                  >
                    {includeTest
                      ? "No sponsors match this filter."
                      : "No sponsors match this filter. Enable 'Include test/demo data' to see seeded records."}
                  </td>
                </tr>
              )}
              {filtered &&
                filtered.map((r) => (
                  <tr
                    key={r.sponsor_id}
                    data-testid={`sp-row-${r.sponsor_slug}`}
                    onClick={() => (window.location.href = `/sponsor-dashboard/${r.sponsor_id}`)}
                    className="border-t border-white/5 hover:bg-white/[0.04] cursor-pointer"
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="text-white">{r.sponsor_name}</span>
                        {r.is_test && (
                          <span
                            className="text-[9px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-[#B4795C]/20 text-[#F3D6A6] border border-[#B4795C]/40"
                            data-testid="sp-test-badge"
                          >
                            test
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-[#8FA894]">{r.sponsor_type}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.audience_size ?? "—"}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.landing_visits}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.referral_clicks}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.signups}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.active_users}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">
                      {r.total_reach_rate == null ? "—" : `${r.total_reach_rate}%`}
                    </td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">
                      {r.active_adoption_rate == null ? "—" : `${r.active_adoption_rate}%`}
                    </td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.trials}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.paid}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.gifts}</td>
                    <td className="px-3 py-2 text-right text-[#B7D2AF]">{r.conversion_rate}%</td>
                    <td className="px-3 py-2 text-[#8FA894]">
                      {r.last_activity ? r.last_activity.slice(0, 10) : "—"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      </section>
    </div>
  );
}
